import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  BarChart3,
  Check,
  Dumbbell,
  Info,
  Mars,
  Sprout,
  Target,
  TrendingUp,
  Trophy,
  Venus,
  X,
  Zap,
} from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/ffmi-calculator")({
  component: FFMICalculator,
  head: () => ({
    meta: seo({
      title:
        "FFMI Hesaplayıcı - Fat Free Mass Index Kas Kütlesi Analizi | AtalayTheCoach",
      description:
        "Kas kütlenizi ve doğal limit potansiyelinizi analiz edin. Bodybuilding ve fitness için bilimsel FFMI değerlendirmesi.",
      keywords:
        "FFMI hesaplama, fat free mass index, kas kütlesi, doğal limit, bodybuilding analizi, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/ffmi-calculator",
    }),
  }),
});

// FFMI Hesaplama Fonksiyonu
function calculateFFMI(
  weight: number,
  height: number,
  bodyFatPercentage: number,
): number {
  const heightInMeters = height / 100;
  const fatMass = (weight * bodyFatPercentage) / 100;
  const fatFreeMass = weight - fatMass;
  const ffmi = fatFreeMass / (heightInMeters * heightInMeters);
  return ffmi;
}

function calculateNormalizedFFMI(ffmi: number, height: number): number {
  const heightInMeters = height / 100;
  return ffmi + 6.1 * (1.8 - heightInMeters);
}

const getFFMICategory = (normalizedFFMI: number, gender: string) => {
  const categories = {
    male: [
      {
        min: 0,
        max: 16,
        level: "Düşük",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Kas kütlesi yetersiz",
      },
      {
        min: 16,
        max: 17,
        level: "Ortalama Altı",
        color: "bg-accent/20 text-accent-foreground border-accent/30",
        desc: "Gelişim potansiyeli var",
      },
      {
        min: 17,
        max: 18,
        level: "Ortalama",
        color: "bg-muted/30 text-muted-foreground border-muted",
        desc: "Normal kas kütlesi",
      },
      {
        min: 18,
        max: 20,
        level: "Ortalama Üzeri",
        color: "bg-primary/10 text-primary border-primary/20",
        desc: "İyi kas gelişimi",
      },
      {
        min: 20,
        max: 22,
        level: "Mükemmel",
        color: "bg-primary/20 text-primary border-primary/30",
        desc: "Çok iyi kas kütlesi",
      },
      {
        min: 22,
        max: 23,
        level: "Elit",
        color: "bg-accent/30 text-accent-foreground border-accent",
        desc: "Elit seviye kas kütlesi",
      },
      {
        min: 23,
        max: 25,
        level: "Süper Elit",
        color: "bg-primary/30 text-primary border-primary/40",
        desc: "Doğal limit yakın",
      },
      {
        min: 25,
        max: 100,
        level: "Doğal Limit Üzeri",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Şüpheli seviye",
      },
    ],
    female: [
      {
        min: 0,
        max: 14,
        level: "Düşük",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Kas kütlesi yetersiz",
      },
      {
        min: 14,
        max: 15,
        level: "Ortalama Altı",
        color: "bg-accent/20 text-accent-foreground border-accent/30",
        desc: "Gelişim potansiyeli var",
      },
      {
        min: 15,
        max: 16,
        level: "Ortalama",
        color: "bg-muted/30 text-muted-foreground border-muted",
        desc: "Normal kas kütlesi",
      },
      {
        min: 16,
        max: 17,
        level: "Ortalama Üzeri",
        color: "bg-primary/10 text-primary border-primary/20",
        desc: "İyi kas gelişimi",
      },
      {
        min: 17,
        max: 18,
        level: "Mükemmel",
        color: "bg-primary/20 text-primary border-primary/30",
        desc: "Çok iyi kas kütlesi",
      },
      {
        min: 18,
        max: 19,
        level: "Elit",
        color: "bg-accent/30 text-accent-foreground border-accent",
        desc: "Elit seviye kas kütlesi",
      },
      {
        min: 19,
        max: 20,
        level: "Süper Elit",
        color: "bg-primary/30 text-primary border-primary/40",
        desc: "Doğal limit yakın",
      },
      {
        min: 20,
        max: 100,
        level: "Doğal Limit Üzeri",
        color: "bg-destructive/10 text-destructive border-destructive/20",
        desc: "Şüpheli seviye",
      },
    ],
  };

  const genderCategories = categories[gender as keyof typeof categories];

  for (const category of genderCategories) {
    if (normalizedFFMI >= category.min && normalizedFFMI < category.max) {
      return category;
    }
  }

  return genderCategories[genderCategories.length - 1];
};

// Doğal Potansiyel Hesaplama
function calculateNaturalPotential(height: number, gender: string) {
  const maxFFMI = gender === "male" ? 25 : 20;
  const heightInMeters = height / 100;
  const maxLeanMass = maxFFMI * (heightInMeters * heightInMeters);

  return {
    maxFFMI,
    maxLeanMass: maxLeanMass,
    maxWeightAt10BF: maxLeanMass / 0.9, // %10 vücut yağında maksimum kilo
    maxWeightAt15BF: maxLeanMass / 0.85, // %15 vücut yağında maksimum kilo
  };
}

function FFMICalculator() {
  const [gender, setGender] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [bodyFat, setBodyFat] = useState<string>("");
  const [results, setResults] = useState<{
    ffmi: number;
    normalizedFFMI: number;
    category: any;
    fatFreeMass: number;
    fatMass: number;
    naturalPotential: any;
  } | null>(null);

  const calculateResults = () => {
    const heightNum = Number.parseFloat(height);
    const weightNum = Number.parseFloat(weight);
    const bodyFatNum = Number.parseFloat(bodyFat);

    if (!heightNum || !weightNum || !bodyFatNum || !gender) return;

    if (bodyFatNum < 3 || bodyFatNum > 50) {
      alert("Vücut yağ oranı 3-50% arasında olmalıdır");
      return;
    }

    const ffmi = calculateFFMI(weightNum, heightNum, bodyFatNum);
    const normalizedFFMI = calculateNormalizedFFMI(ffmi, heightNum);
    const category = getFFMICategory(ffmi, gender);

    const fatMass = (weightNum * bodyFatNum) / 100;
    const fatFreeMass = weightNum - fatMass;

    const naturalPotential = calculateNaturalPotential(heightNum, gender);

    setResults({
      ffmi,
      normalizedFFMI,
      category,
      fatFreeMass,
      fatMass,
      naturalPotential,
    });
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-5xl px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
              <Dumbbell className="h-12 w-12 text-primary" />
            </div>
          </div>
          <h1 className="mb-6 font-bold text-4xl text-foreground leading-tight md:text-5xl lg:text-6xl">
            FFMI Hesaplayıcı
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Fat-Free Mass Index (FFMI) ile kas kütlenizi analiz edin. Doğal
            potansiyelinizi keşfedin ve hedeflerinizi belirleyin.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {/* Hesaplayıcı Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Dumbbell className="h-5 w-5 text-primary" />
                  FFMI Hesapla
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <Label>Cinsiyet</Label>
                    <RadioGroup value={gender} onValueChange={setGender}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="male-ffmi" />
                        <Label htmlFor="male-ffmi">Erkek</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="female-ffmi" />
                        <Label htmlFor="female-ffmi">Kadın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="height">Boy (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      placeholder="175"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Kilo (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      placeholder="75"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bodyFat">Vücut Yağ Oranı (%)</Label>
                    <Input
                      id="bodyFat"
                      type="number"
                      placeholder="12"
                      step="0.1"
                      value={bodyFat}
                      onChange={(e) => setBodyFat(e.target.value)}
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-primary/20 bg-primary/10 p-4 dark:border-primary/30 dark:bg-primary/20">
                  <h4 className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                    <Info className="h-4 w-4 text-primary" />
                    Vücut Yağ Oranını Nasıl Öğrenebilirim?
                  </h4>
                  <p className="mb-3 text-muted-foreground text-sm">
                    Doğru FFMI hesaplama için vücut yağ oranınızı bilmeniz
                    gerekiyor:
                  </p>
                  <ul className="space-y-1 text-muted-foreground text-sm">
                    <li>
                      •{" "}
                      <strong className="text-foreground">
                        Body Fat Hesaplayıcımızı
                      </strong>{" "}
                      kullanın (Kaliper, Navy method)
                    </li>
                    <li>
                      •{" "}
                      <strong className="text-foreground">DEXA taraması</strong>{" "}
                      yaptırın (en hassas)
                    </li>
                    <li>
                      • <strong className="text-foreground">BodPod</strong> veya
                      su altı tartımı
                    </li>
                    <li>
                      •{" "}
                      <strong className="text-foreground">
                        Bioelectrical impedance
                      </strong>{" "}
                      terazisi (yaklaşık)
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={calculateResults}
                  className="w-full"
                  disabled={!height || !weight || !bodyFat || !gender}
                >
                  FFMI Hesapla
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Sonuçlar */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Sonuçlarınız
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results ? (
                  <div className="space-y-6">
                    <div className="rounded-lg bg-primary/10 p-6 text-center">
                      <div className="mb-2 font-bold text-3xl text-primary">
                        {results.normalizedFFMI.toFixed(1)}
                      </div>
                      <div className="text-muted-foreground text-sm">
                        Normalize FFMI
                      </div>
                      <div className="mt-1 text-muted-foreground text-xs">
                        (Ham FFMI: {results.ffmi.toFixed(1)})
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Kategori:</h4>
                      <div
                        className={`rounded-lg p-3 ${results.category.color}`}
                      >
                        <div className="text-center font-bold">
                          {results.category.level}
                        </div>
                        <div className="mt-1 text-center text-xs">
                          {results.category.desc}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Vücut Kompozisyonu:</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-primary/20 bg-primary/10 p-3 text-center">
                          <div className="font-bold text-primary">
                            {results.fatFreeMass.toFixed(1)} kg
                          </div>
                          <div className="text-muted-foreground text-xs">
                            Yağsız Kütle
                          </div>
                        </div>
                        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-center">
                          <div className="font-bold text-destructive">
                            {results.fatMass.toFixed(1)} kg
                          </div>
                          <div className="text-muted-foreground text-xs">
                            Yağ Kütlesi
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold">Doğal Potansiyel:</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 p-2">
                          <span>Maksimum FFMI:</span>
                          <span className="font-bold text-primary">
                            {results.naturalPotential.maxFFMI}
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 p-2">
                          <span>Maks Yağsız Kütle:</span>
                          <span className="font-bold text-primary">
                            {results.naturalPotential.maxLeanMass.toFixed(1)} kg
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-border bg-accent/30 p-2">
                          <span>%10 yağda maks kilo:</span>
                          <span className="font-bold text-foreground">
                            {results.naturalPotential.maxWeightAt10BF.toFixed(
                              1,
                            )}{" "}
                            kg
                          </span>
                        </div>
                        <div className="flex items-center justify-between rounded-lg border border-muted bg-muted/50 p-2">
                          <span>%15 yağda maks kilo:</span>
                          <span className="font-bold text-foreground">
                            {results.naturalPotential.maxWeightAt15BF.toFixed(
                              1,
                            )}{" "}
                            kg
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="rounded border border-info/20 bg-info/5 p-3 dark:border-info/30 dark:bg-info/10">
                      <p className="text-info-foreground text-sm">
                        <strong>Not:</strong> FFMI 25+ (erkek) / 20+ (kadın)
                        değerleri doğal olmayan yardım kullanımını işaret
                        edebilir.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground">
                    Hesaplama yapmak için soldaki formu doldurun
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Modern Bilgilendirici İçerik */}
        <div className="mt-16 space-y-12">
          {/* Hero Section */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 md:p-12">
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <h2 className="mb-4 font-bold text-3xl text-foreground">
                  Kas Potansiyelinizi Keşfedin
                </h2>
                <p className="mb-6 text-lg text-muted-foreground">
                  FFMI, vücut ağırlığı ve boy farklılıklarını elemleyerek gerçek
                  kas kütlenizi objektif olarak değerlendirir.
                </p>
              </div>
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-primary/10 p-4 dark:bg-primary/20">
                    <Dumbbell className="h-12 w-12 text-primary" />
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">
                  Bilimsel kas kütlesi analizi
                </div>
              </div>
            </div>
          </div>

          {/* FFMI vs BMI Comparison */}
          <div className="grid gap-8 md:grid-cols-2">
            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <Dumbbell className="h-10 w-10 " />
                </div>
                <h3 className="font-bold text-xl">FFMI</h3>
                <p className="dark:/70 text-muted-foreground text-sm">
                  Fat-Free Mass Index
                </p>
              </div>
              <ul className="space-y-2 text-foreground text-sm">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>Kas kütlesini ölçer</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>Vücut yağından bağımsız</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>Doğal limit belirleme</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>Bodybuilding için ideal</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border bg-card p-6 text-foreground">
              <div className="mb-4 text-center">
                <div className="mb-2 flex justify-center">
                  <BarChart3 className="h-10 w-10 text-accent-foreground" />
                </div>
                <h3 className="font-bold text-accent-foreground text-xl">
                  BMI
                </h3>
                <p className="text-muted-foreground text-sm">Body Mass Index</p>
              </div>
              <ul className="space-y-2 text-foreground text-sm">
                <li className="flex items-center gap-2">
                  <X className="h-4 w-4 text-destructive" />
                  <span>Kas ve yağı ayırt etmez</span>
                </li>
                <li className="flex items-center gap-2">
                  <X className="h-4 w-4 text-destructive" />
                  <span>Kaslı sporcular "obez" çıkar</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>Genel sağlık için kolay</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary" />
                  <span>Vücut yağı gerekmez</span>
                </li>
              </ul>
            </div>
          </div>

          {/* FFMI Categories Visual */}
          <div className="rounded-2xl border border-border/50 bg-card p-8">
            <h3 className="mb-8 text-center font-bold text-2xl text-foreground">
              FFMI Kategorileri
            </h3>

            <div className="grid gap-8 md:grid-cols-2">
              <div>
                <h4 className="mb-4 flex items-center justify-center gap-2 text-center font-semibold text-primary text-xl">
                  <Mars className="h-5 w-5" />
                  Erkekler
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      range: "16-17",
                      category: "Ortalama Altı",
                      desc: "Gelişim gerekli",
                    },
                    {
                      range: "17-18",
                      category: "Ortalama",
                      desc: "Normal seviye",
                    },
                    {
                      range: "18-20",
                      category: "Ortalama Üzeri",
                      desc: "İyi kas gelişimi",
                    },
                    {
                      range: "20-22",
                      category: "Mükemmel",
                      desc: "Çok iyi seviye",
                    },
                    {
                      range: "22-23",
                      category: "Elit",
                      desc: "Elit kas kütlesi",
                    },
                    {
                      range: "23-25",
                      category: "Süper Elit",
                      desc: "Doğal limit",
                    },
                    {
                      range: "25+",
                      category: "Şüpheli",
                      desc: "Doğal olmayabilir",
                    },
                  ].map((item) => (
                    <div className="flex items-center gap-4 rounded-lg border border-border/50 bg-card/70 p-3 dark:border-border/30 dark:bg-card/50">
                      <div className={`h-4 w-4 rounded-full`}></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">
                            {item.category}
                          </span>
                          <span className="font-mono text-muted-foreground text-sm">
                            {item.range}
                          </span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="mb-4 flex items-center justify-center gap-2 text-center font-semibold text-accent-foreground text-xl">
                  <Venus className="h-5 w-5" />
                  Kadınlar
                </h4>
                <div className="space-y-3">
                  {[
                    {
                      range: "14-15",
                      category: "Ortalama Altı",
                      desc: "Gelişim gerekli",
                    },
                    {
                      range: "15-16",
                      category: "Ortalama",
                      desc: "Normal seviye",
                    },
                    {
                      range: "16-17",
                      category: "Ortalama Üzeri",
                      desc: "İyi kas gelişimi",
                    },
                    {
                      range: "17-18",
                      category: "Mükemmel",
                      desc: "Çok iyi seviye",
                    },
                    {
                      range: "18-19",
                      category: "Elit",
                      desc: "Elit kas kütlesi",
                    },
                    {
                      range: "19-20",
                      category: "Süper Elit",
                      desc: "Doğal limit",
                    },
                    {
                      range: "20+",
                      category: "Şüpheli",
                      desc: "Doğal olmayabilir",
                    },
                  ].map((item) => (
                    <div className="flex items-center gap-4 rounded-lg border border-border/50 bg-card/70 p-3 dark:border-border/30 dark:bg-card/50">
                      <div className={`h-4 w-4 rounded-full`}></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-foreground">
                            {item.category}
                          </span>
                          <span className="font-mono text-muted-foreground text-sm">
                            {item.range}
                          </span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-primary/20 bg-primary/10 p-4 dark:border-primary/30 dark:bg-primary/20">
              <div className="flex items-start justify-center gap-3">
                <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
                <p className="text-center text-muted-foreground text-sm">
                  <strong className="text-foreground">Normalize FFMI:</strong>{" "}
                  Boy farkı düzeltmesi yapılmış FFMI değeri. 180cm referans boy
                  kullanılır.
                </p>
              </div>
            </div>
          </div>

          {/* Natural Limits */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 text-foreground">
            <h3 className="mb-6 flex items-center gap-2 font-bold text-2xl">
              <Target className="h-6 w-6 text-primary" />
              Doğal Kas Geliştirme Limitleri
            </h3>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-xl border border-primary/10 bg-primary/5 p-6 dark:border-primary/20 dark:bg-primary/10">
                <div className="mb-4 text-center">
                  <div className="mb-2 flex justify-center">
                    <Sprout className="h-8 w-8 text-primary" />
                  </div>
                  <h4 className="font-bold text-primary">Başlangıç</h4>
                </div>
                <ul className="space-y-2 text-foreground text-sm">
                  <li>• İlk 1 yıl: Hızlı gelişim</li>
                  <li>• Ayda 1-2 kg kas kazanımı</li>
                  <li>• Temel egzersizlere odaklanın</li>
                  <li>• Doğru form öğrenin</li>
                </ul>
              </div>

              <div className="rounded-xl border border-accent/10 bg-accent/5 p-6 dark:border-accent/20 dark:bg-accent/10">
                <div className="mb-4 text-center">
                  <div className="mb-2 flex justify-center">
                    <TrendingUp className="h-8 w-8 text-accent-foreground" />
                  </div>
                  <h4 className="font-bold text-accent-foreground">
                    Orta Seviye
                  </h4>
                </div>
                <ul className="space-y-2 text-foreground text-sm">
                  <li>• 2-3 yıl: Yavaşlayan gelişim</li>
                  <li>• Ayda 0.5-1 kg kas kazanımı</li>
                  <li>• Programlı periodizasyon</li>
                  <li>• Beslenme optimizasyonu</li>
                </ul>
              </div>

              <div className="rounded-xl border border-destructive/10 bg-destructive/5 p-6 dark:border-destructive/20 dark:bg-destructive/10">
                <div className="mb-4 text-center">
                  <div className="mb-2 flex justify-center">
                    <Trophy className="h-8 w-8 text-destructive" />
                  </div>
                  <h4 className="font-bold text-destructive">İleri Seviye</h4>
                </div>
                <ul className="space-y-2 text-foreground text-sm">
                  <li>• 4+ yıl: Minimal gelişim</li>
                  <li>• Yılda 1-2 kg kas kazanımı</li>
                  <li>• Doğal limite yaklaşım</li>
                  <li>• Uzman rehberliği</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 rounded-lg border border-muted bg-muted/30 p-4 dark:border-muted/50 dark:bg-muted/20">
              <h4 className="mb-2 flex items-center gap-2 font-semibold">
                <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                Önemli Notlar
              </h4>
              <ul className="space-y-1 text-foreground text-sm">
                <li>
                  • FFMI 25+ (erkek) / 20+ (kadın) değerleri doğal olmayan
                  yardım kullanımını işaret edebilir
                </li>
                <li>
                  • Genetik farklılıklar sonucu bazı kişiler bu limitlerin
                  dışında olabilir
                </li>
                <li>
                  • Sporcular ve elite düzey antrenmanlar istisnai durumlar
                  yaratabilir
                </li>
                <li>
                  • Bu değerler rehber niteliğindedir, kesin tanı aracı değildir
                </li>
              </ul>
            </div>
          </div>

          {/* FFMI Improvement Tips */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 text-foreground">
            <h3 className="mb-6 flex items-center gap-2 font-bold text-2xl">
              <Zap className="h-6 w-6 text-primary" />
              FFMI Geliştirme Stratejileri
            </h3>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Progressive Overload
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Düzenli olarak ağırlık, tekrar veya set sayısını artırın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Bileşik Egzersizler
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Squat, deadlift, bench press gibi temel hareketlere
                      odaklanın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Yeterli Protein
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Günde vücut ağırlığınız × 1.6-2.2g protein alın
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Kalori Fazlası
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Kas kazanımı için TDEE + 200-500 kalori alın
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    5
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Dinlenme ve Uyku
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Gece 7-9 saat kaliteli uyku ve kaslar arası dinlenme
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground text-sm shadow-sm">
                    6
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">
                      Tutarlılık
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Uzun vadeli tutarlı antrenman en önemli faktördür
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
